import { Stream } from "./stream"

/**
 * A building block for the {@link Graph} and {@link Node} inputs and outputs.
 * @internal
 */
export abstract class Port<T> {
  protected readonly _stream = new Stream<T>()

  /** Performs one-off write to the Port. */
  protected async write(data: T) {
    try {
      const writer = this._stream.getWriter()
      writer.write(data)
      writer.releaseLock()
    } catch {
      throw new Error(
        "Can not perform write: the Port is already connected as a destination to another Port.",
      )
    }
  }

  /** Returns an iterable allowing to read the port‘s data. */
  protected async *data() {
    const reader = this._stream.getReader()

    try {
      while (true) {
        const { value, done } = await reader.read()

        if (done) return

        yield value
      }
    } finally {
      reader.cancel()
    }
  }

  /**
   * Continuously pipes the port‘s data to the target port.
   * @returns a "disconnect" function that can be called to cancel the piping.
   */
  protected connect(target: Port<T>) {
    return this._stream.pipe(target._stream)
  }

  /** Closes the port terminating all the port‘s data iterators */
  protected close() {
    return this._stream.close()
  }
}
